import React, { useState, useContext, SyntheticEvent, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import Toolbar from '@mui/material/Toolbar';

// MUI Icons
import PasswordIcon from '@mui/icons-material/Password';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonIcon from '@mui/icons-material/Person';
import SyncIcon from '@mui/icons-material/Sync';

// Custom Components
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogHeader from '@/ui/Dialog/DialogHeader';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogBody from '@/ui/Dialog/DialogBody';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItem from '@/ui/Dialog/DialogToolbarMenuItem';
import DialogToolbarMenuItemDelete from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDelete';
import DialogToolbarMenuItemActivate from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemActivate';
import DialogToolbarMenuItemDeactivate from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDeactivate';
import DialogContext from '@/ui/DialogContext/DialogContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import { GridContainer, GridItem } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import FormAvatar from "@/ui/Form/FormAvatar";
import { Permissions } from '@/lib/permissions';
import { userService } from '@/services/userService';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';
import { IDialogProps } from '@/@types/ui/DialogContext';
import { ConfirmResult } from '@/@types/lib/confirmDialog';
import UserContext from '@/components/UserContext/UserContext';
import PasswordSetDialog from '@/views/UsersPage/PasswordSetDialog';

const UserDialog: FunctionComponent<IDialogProps> = (props) => {
  const dialogContext = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const [syncChange, setSyncChange] = useState(false);

  const { dc, mode, form, onClose } = props;

  const STATUS_REGISTRIRAN = 10;
  const STATUS_AKTIVAN = 20;
  const STATUS_ONEMOGUCEN_HPS = 29;
  const STATUS_NEAKTIVAN = 30;
  const STATUS_ANONIMIZIRAN = 40;

  const { record, validation, dataChanged, fields, subModels, onFieldChange, doInsert, doUpdate, doClose, doDelete, doRefresh } =
    useFormController(props as FormControllerProps);

  const handleInsert = (evt: SyntheticEvent) => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            const msg = t('notifications.insert');
            snackbarContext.showNotification(msg, 'success');
            close({ dataChanged: true, action: 'insert' });
          } else if (result.error) {
            const msg = t('error.addRecord');
            snackbarContext.showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          // wont ever be triggered cuz handleInsert never rejects promise
          snackbarContext.showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = (evt: SyntheticEvent) => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = t('notifications.update');
            snackbarContext.showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            // will not be triggered cauz handleUpdate rejects errors (so the catch under here will be triggered)
            const msg = t('error.editRecord');
            snackbarContext.showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          const msg = t('error.editRecord');
          snackbarContext.showNotification(msg, 'error');
        });
    }
  };

  const handleClose = (evt: SyntheticEvent) => {
    if (doClose) {
      doClose()
        .then((result) => {
          if (result.success) {
            close({ dataChanged: false, action: 'cancel' });
          } else {
            if (result.shouldSave) {
              handleUpdate(evt);
            } else if (result.canceled) {
              // do nothing
            }
          }
        })
        .catch((e) => console.log(e));
    }
  };

  const handleActivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        userService.activate(Number(record.id)).then((result) => {
          if (result) {
            const msg = t('messages.activate_user_success');
            snackbarContext.showNotification(msg, 'success');
            doRefresh();
            onClose({ dataChanged: true, action: 'update' });
          } else {
            const msg = t('error.activate_user');
            snackbarContext.showNotification(msg, 'error');
          }
        });
      } catch (error) {
        const msg = t('error.activate_user');
        snackbarContext.showNotification(msg, 'error');
      }
    }
  };

  const handleDeactivate = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        userService.deactivate(Number(record.id)).then((result) => {
          if (result) {
            const msg = t('messages.deactivate_user_success');
            snackbarContext.showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else {
            const msg = t('error.deactiavate_user');
            snackbarContext.showNotification(msg, 'error');
          }
        });
      } catch (error) {
        const msg = t('error.deactivate_user');
        snackbarContext.showNotification(msg, 'error');
      }
    }
  };

  const handlePasswordSet = () => {
    dialogContext.showDialog(
      PasswordSetDialog,
      {
        dc: dc,
        form: 'password_set',
        userId: record.id,
      },
      2
    );
  };

  const confirmAnonymize = (): Promise<ConfirmResult> => {
    const title = t('cdialogs.anonimize_warning');
    const text = t('cdialogs.close_text');
    const confirmButtonText = t('cdialogs.yes_save_changes');
    const cancelButtonText = t('cdialogs.no_just_close');

    return dialogContext.showConfirmDialog({ title, text, confirmButtonText, cancelButtonText });
  };

  const handleAnonymize = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      try {
        confirmAnonymize().then((result) => {
          if (result.confirmed) {
            try {
              userService.anonymize(Number(record.id)).then((result) => {
                if (result) {
                  const msg = t('messages.anonymize_success');
                  snackbarContext.showNotification(msg, 'success');
                  close({ dataChanged: true, action: 'update' });
                } else {
                  const msg = t('error.anonymize');
                  snackbarContext.showNotification(msg, 'error');
                }
              });
            } catch (error) {
              const msg = t('error.anonymize');
              snackbarContext.showNotification(msg, 'error');
            }
          }
        });
      } catch (error) {
        const msg = t('error.anonymize');
        snackbarContext.showNotification(msg, 'error');
      }
    }
  };

  const close = (result: ClosingDetails) => {
    if (result.dataChanged || dataChanged  || syncChange) {
      onClose({ dataChanged: true, action: result.action });
    }
    dialogContext.hideDialog();
  };

  const handleHPSSync = (evt: SyntheticEvent) => {
    if (record.id !== null) {
      userService.hpsSync(Number(record.id)).then((result) => {
        if (result) {
          const msg = t('messages.hps_sync_success');
          snackbarContext.showNotification(msg, 'success');
          setSyncChange(true);
          doRefresh();
        } else {
          const msg = t('error.hps_sync');
          snackbarContext.showNotification(msg, 'error');
        }
      })
        .catch((resp) => {
          const msg = t('error.hps_sync');
          snackbarContext.showNotification(msg, 'error');
        });
    }
  }

  const handleDelete = (evt: SyntheticEvent) => {
    if (doDelete) {
      doDelete().then((result) => {
        if (result.success) {
          const msg = t('messages.user_deleted');
          snackbarContext.showNotification(msg, 'success');
          close({ dataChanged: true, action: 'update' });
        } else if (result.error) {
          const msg = t('error.delete') + result.error;
          snackbarContext.showNotification(msg, 'error');
        }
      });
    }
  };

  let cmode = mode;
  if (record.status === STATUS_REGISTRIRAN || record.status === STATUS_NEAKTIVAN || record.status === STATUS_ANONIMIZIRAN) {
    cmode = 'view';
  }

  const canSetPassword =
    userContext?.hasAnyPermission([Permissions.Administration])
      || (userContext?.hasAnyPermission([Permissions.AdministrationOrgUsers])
        && userContext?.organisationId === record.owner_organisation_id) ? true : false;

  const canDeactivate = record.status === STATUS_AKTIVAN || record.status === STATUS_ONEMOGUCEN_HPS;

  const canActivate = record.status === STATUS_NEAKTIVAN;

  const canAnonymize =
    userContext?.hasAnyPermission([Permissions.Administration])
    && record.status !== STATUS_ANONIMIZIRAN;

  const canDelete =
    userContext?.hasAnyPermission([Permissions.Administration])
      || (userContext?.hasAnyPermission([Permissions.AdministrationOrgUsers])
        && userContext?.organisationId === record.owner_organisation_id) ? true : false;

  const canHPSSync = record.owner_organisation_id === 30
    && (record.user_role_id === 33 || record.user_role_id === 37 || record.user_role_id === 36 || record.user_role_id === 39)
    && (userContext?.hasAnyPermission([Permissions.Administration])
      || (userContext?.hasAnyPermission([Permissions.AdministrationOrgUsers])
        && userContext?.organisationId === record.owner_organisation_id)) ? true : false;

  const hasAnyAllowedAction = canSetPassword || canDeactivate || canActivate || canAnonymize || canDelete || canHPSSync;

  const columnWidth = mode === "insert" ? 3 : 4;

  return (
    <DynamicDialog open maxWidth='xl' onClose={handleClose}>
      <DialogHeader>
        <Toolbar variant="dense" disableGutters>
          <FormAvatar ariaLabel="avatar" icon={<PersonIcon />} />
          <DialogToolbarHeading>{t('titles.user')}</DialogToolbarHeading>
          <ToolbarFillContent />
          {hasAnyAllowedAction ? (
            <DialogToolbarMenuButton>
              {canSetPassword ? (
                <DialogToolbarMenuItem
                  onClick={handlePasswordSet}
                  label={t('buttons.password_set')}
                  icon={<PasswordIcon />}
                />
              ) : null}
              {canDeactivate ? (
                <DialogToolbarMenuItemDeactivate onClick={handleDeactivate} />
              ) : null}
              {canActivate ? (
                <DialogToolbarMenuItemActivate onClick={handleActivate} />
              ) : null}
              {canAnonymize ? (
                <DialogToolbarMenuItem
                  onClick={handleAnonymize}
                  label={t('buttons.anonymize')}
                  icon={<AccountCircleIcon />}
                />
              ) : null}
              {canHPSSync ? (
                <DialogToolbarMenuItem
                  onClick={handleHPSSync}
                  label={t('buttons.hps_sync')}
                  icon={<SyncIcon />}
                />
              ) : null}
              {canDelete ? (
                <DialogToolbarMenuItemDelete onClick={handleDelete} />
              ) : null}
            </DialogToolbarMenuButton>
          ) : null}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogHeader>
      <DialogBody>
        <Box m={2}>
          <GridContainer>
            {mode === "insert" ?
              <GridItem size={{ xs: 12, sm: columnWidth }}>
                <GridContainer>
                  <FormContent
                    title={t("titles.pristupni_podaci")}
                    fieldNames={['email', 'password', 'password_confirm']}
                    record={record}
                    validation={validation}
                    fields={fields}
                    onFieldChange={onFieldChange}
                    mode={cmode}
                    columns={1}
                    subModels={subModels}
                  />
                </GridContainer>
              </GridItem>
              : null}
            <GridItem size={{ xs: 12, sm: columnWidth }}>
              <GridContainer>
                <FormContent
                  title={t("titles.osobni_podaci")}
                  fieldNames={['first_name', 'last_name', 'oib', 'mobitel', 'email']}
                  record={record}
                  validation={validation}
                  fields={fields}
                  onFieldChange={onFieldChange}
                  mode={cmode}
                  columns={1}
                  subModels={subModels}
                />
              </GridContainer>
            </GridItem>
            <GridItem size={{ xs: 12, sm: columnWidth }}>
              <GridContainer>
                <FormContent
                  title={t("titles.hps_podaci")}
                  fieldNames={['hps_plan_udruga_id', 'hps_spv_id', 'hps_broj_vodicke_znacke', 'hps_has_licenca', 'hps_licenca_do']}
                  record={record}
                  validation={validation}
                  fields={fields}
                  onFieldChange={onFieldChange}
                  mode={cmode}
                  columns={1}
                  subModels={subModels}
                />
              </GridContainer>
            </GridItem>
            <GridItem size={{ xs: 12, sm: columnWidth }}>
              <GridContainer>
                <FormContent
                  title={t("titles.dozvole")}
                  fieldNames={['owner_organisation_id', 'user_role_id', 'status','remarks']}
                  record={record}
                  validation={validation}
                  fields={fields}
                  onFieldChange={onFieldChange}
                  mode={cmode}
                  columns={1}
                  subModels={subModels}
                />
              </GridContainer>
            </GridItem>
          </GridContainer>
        </Box>
      </DialogBody>
      <DialogActions>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default UserDialog;
